// Categorias dos Ajustes da IA (D1, D11, mockup r3 `#ajustes/*`): uma página
// por área da Central, com o mesmo nome, mais a Visão geral e Modelos e
// agentes. Cada categoria reaproveita o painel que já existia (plano 4.4); a
// tela só organiza. `areaId` liga a categoria ao módulo da conta: categoria de
// módulo não liberado não aparece.
import { areaById, isAiCenterModuleEnabled } from './aiCenterAreas';

export const AI_SETTINGS_HOME = 'home';

// Grupos da navegação, na ordem da tela (mesmos grupos do trilho da Central).
export const AI_SETTINGS_GROUPS = ['customer', 'internal', 'base'];

// `enabledKey`: chave de GET ai_center/settings que liga a área; o cabeçalho
// da categoria mostra o interruptor dela. `tabs`: sub-abas (a primeira é a
// página da categoria, sem seção no endereço).
const DEFINITIONS = [
  { id: AI_SETTINGS_HOME, icon: 'i-ph-house-simple', group: '' },
  {
    id: 'comentarios',
    areaId: 'social',
    group: 'customer',
    enabledKey: 'social_enabled',
    tabs: ['', 'tipos', 'campanha', 'conexoes', 'catalogo'],
    // Catálogo de imóveis só com o Módulo Imobiliária (27/09/2026).
    tabAreas: { catalogo: 'properties' },
  },
  {
    id: 'retomadas',
    areaId: 'opportunities',
    group: 'customer',
    enabledKey: 'opportunity_enabled',
    tabs: ['', 'situacoes', 'autonomia', 'formato'],
  },
  {
    id: 'copilot',
    areaId: 'copilot',
    group: 'customer',
    tabs: ['', 'agentes'],
  },
  { id: 'kanban', areaId: 'kanban', group: 'internal', tabs: ['', 'funis'] },
  {
    id: 'conversoes',
    areaId: 'conversions',
    group: 'internal',
    tabs: ['', 'integracoes'],
  },
  {
    id: 'imoveis',
    areaId: 'properties',
    group: 'internal',
    enabledKey: 'property_enabled',
  },
  {
    id: 'ligacoes',
    areaId: 'calls',
    group: 'internal',
    tabs: ['', 'telefonia'],
  },
  { id: 'modelos', icon: 'i-ph-cpu', group: 'base' },
];

const keyOf = id => id.toUpperCase();

export const AI_SETTINGS_CATEGORY_LIST = DEFINITIONS.map(category => ({
  tabs: [],
  ...category,
  icon: category.icon || areaById(category.areaId)?.icon || '',
  labelKey: `AI_SETTINGS.CATEGORIES.${keyOf(category.id)}.TITLE`,
  descriptionKey: `AI_SETTINGS.CATEGORIES.${keyOf(category.id)}.DESCRIPTION`,
  keywordsKey: `AI_SETTINGS.CATEGORIES.${keyOf(category.id)}.KEYWORDS`,
  areaSlug: areaById(category.areaId)?.slug || '',
}));

export const settingsCategoryById = id =>
  AI_SETTINGS_CATEGORY_LIST.find(category => category.id === id) || null;

// Sub-abas que a conta tem: a de outro módulo só com ele liberado.
export const visibleSettingsTabs = (category, isEnabled) =>
  (category?.tabs || []).filter(tab => {
    const areaId = category.tabAreas?.[tab];
    return !areaId || isAiCenterModuleEnabled(areaId, isEnabled);
  });

// Categoria que a conta tem: Visão geral e Modelos e agentes sempre; as
// outras só com o módulo da área liberado.
export const canSeeSettingsCategory = (category, isEnabled) =>
  Boolean(category) &&
  (!category.areaId || isAiCenterModuleEnabled(category.areaId, isEnabled));

export const visibleSettingsCategories = isEnabled =>
  AI_SETTINGS_CATEGORY_LIST.filter(category =>
    canSeeSettingsCategory(category, isEnabled)
  );

// Busca da navegação: pelo nome e pelas palavras-chave de cada categoria
// ("limite", "carência", "peso"). `match: 'keyword'` quando só a palavra-chave
// bateu, para a tela dizer por que a categoria apareceu.
const normalize = value =>
  String(value || '')
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export const searchSettingsCategories = (categories, query, t) => {
  const needle = normalize(query);
  if (!needle) return categories.map(category => ({ category, match: '' }));
  return categories
    .map(category => {
      if (normalize(t(category.labelKey)).includes(needle)) {
        return { category, match: 'name' };
      }
      if (normalize(t(category.keywordsKey)).includes(needle)) {
        return { category, match: 'keyword' };
      }
      return null;
    })
    .filter(Boolean);
};

// Agentes de Modelos e agentes: o endereço usa o nome da área
// (`ajustes/modelos/comentarios`); a API usa o tipo do agente (`social`).
export const AGENT_KIND_SLUGS = {
  social: 'comentarios',
  opportunity: 'retomadas',
  kanban: 'kanban',
  copilot: 'copilot',
  calls: 'ligacoes',
};

export const agentKindForSlug = slug =>
  Object.keys(AGENT_KIND_SLUGS).find(kind => AGENT_KIND_SLUGS[kind] === slug) ||
  '';

// Aceita o tipo antigo da tela de agentes (`?kind=opportunities`).
export const agentSlugForKind = kind => {
  const normalized = kind === 'opportunities' ? 'opportunity' : kind;
  return AGENT_KIND_SLUGS[normalized] || '';
};
